import {captureException} from '@sentry/browser';
import {match} from 'ts-pattern';

import {always, isNotEmpty, isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {browserStorageKey, getWorktreeLoginParams, OAUTH_CLIENT_ID} from '@dms/config';

import {iamRedirectRoute, workspaceRedirectRoute} from '../constants/routes';
import {buildUrl} from './buildUrl';
import {generateOAuthFlow} from './generateOAuthFlow';
import {getCommonParentDomain} from './getCommonParentDomain';
import {getWorkspaceFromUri} from './getWorkspaceFromUri';
import {isLocalDevHost} from './isLocalDevHost';
import {isExternalNavigationClaimed, navigateExternally} from './navigateExternally';
import {writeOAuthFlowCookie} from './writeOAuthFlowCookie';

export enum iamQueryParams {
  ORIGIN_URI = 'origin_uri',
  CODE = 'code',
  STATE = 'state',
  WORKSPACE = 'workspace',
}

const AUTH_CALLBACK_ROUTES: string[] = [iamRedirectRoute, workspaceRedirectRoute];

const parseUrl = (value: string) => {
  try {
    return new URL(value);
  } catch {
    return null;
  }
};

// An auth-callback URL must never become origin_uri — each failed login attempt would nest the
// callback one level deeper, redirecting in a loop between the app and IAM. Unwrap it to the
// user's original destination instead.
const getSafeOriginUri = () => {
  let uri = `${window.location.origin}${window.location.pathname}${isNotNilOrEmpty(window.location.search) ? window.location.search : ''}`;

  let parsed = parseUrl(uri);
  while (isNotNil(parsed) && AUTH_CALLBACK_ROUTES.includes(parsed.pathname)) {
    uri = parsed.searchParams.get(iamQueryParams.ORIGIN_URI) ?? '';
    parsed = parseUrl(uri);
  }

  return isNotNil(parsed) ? uri : window.location.origin;
};

export type RedirectToLoginOptions = {
  currentUserEmail?: string | undefined;
  workspace?: string | undefined;
  redirectUrl?: string | undefined;
  additionalQueryParams?: Record<string, string>;
  page?: 'login' | 'reset-password' | 'user-invitation';
};

export const redirectToLogin = (options?: RedirectToLoginOptions) => {
  if (isExternalNavigationClaimed()) {
    return;
  }

  const iamUrl = process.env.IAM_URL;
  const {workspace} = getWorkspaceFromUri();

  const redirectWorkspace = options?.workspace ?? workspace;

  if (!iamUrl) {
    throw new Error('IAM_URL is not set');
  }

  const page = match(options?.page)
    .with('reset-password', always('reset-password'))
    .with('user-invitation', always('user-invitation'))
    .otherwise(always(''));

  const url = new URL(`${iamUrl}${page}`);

  const nativeRedirect = `${buildUrl(iamRedirectRoute, redirectWorkspace, false)}?${iamQueryParams.ORIGIN_URI}=${encodeURIComponent(getSafeOriginUri())}`;
  const redirect_uri = options?.redirectUrl ?? nativeRedirect;

  const searchParamsConfig = {
    workspace: redirectWorkspace,
    email: options?.currentUserEmail,
    language: localStorage.getItem(browserStorageKey.LAST_KNOWN_LANGUAGE) ?? 'en',
    client_id: OAUTH_CLIENT_ID,
    redirect_uri,
    ...options?.additionalQueryParams,
  };

  Object.entries(searchParamsConfig).forEach(([key, value]) => {
    if (isNotNil(value) && isNotEmpty(value)) {
      url.searchParams.set(key, value);
    }
  });

  // IAM cannot see this app's worktree override (own origin, own storage), so the target is handed
  // over on every redirect — empty value included, otherwise IAM stays on a stale worktree and
  // issues the auth code against the wrong database.
  Object.entries(getWorktreeLoginParams()).forEach(([key, value]) => {
    url.searchParams.set(key, value);
  });
  const legacyUrl = url.toString();

  void navigateExternally(async () => {
    try {
      const hostname = window.location.hostname;
      const callbackHostname = new URL(redirect_uri).hostname;
      const domain = getCommonParentDomain(hostname, callbackHostname);
      if (isLocalDevHost(hostname) && !domain.includes('.') && hostname !== callbackHostname) {
        return legacyUrl;
      }

      const flow = await generateOAuthFlow();
      writeOAuthFlowCookie(flow, redirect_uri);
      url.searchParams.set('code_challenge', flow.challenge);
      url.searchParams.set('code_challenge_method', flow.challengeMethod);
      url.searchParams.set('state', flow.state);
      return url.toString();
    } catch (error) {
      captureException(error);
      return legacyUrl;
    }
  });
};
